import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox";
import { PageHeading } from "@via/components/page-heading";
import Table from "@via/components/table/table";
import { HorizontalTabs } from "@via/components/tabs";
import Modal from "@via/components/modal";
import {
  useAttachCustomerBoundary,
  useDeleteCustomerBoundary,
  useDetachCustomerBoundary,
  useGetCustomerBoundary,
} from "@via/pages/account/admin/admin.api.tsx";
import { useGetOrganizations } from "@via/pages/account/admin/organizations/organizations.api.tsx";
import AddCustomerBoundary from "@via/pages/account/admin/customer-boundary/add-customer-boundary";
import EditCustomerBoundary from "@via/pages/account/admin/customer-boundary/edit-customer-boundary";
import { BUTTONS, ButtonColor } from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import { type Tab } from "@via/utils/interfaces.tsx";
import {
  BoundaryKind,
  ListCustomerBoundaryResponseBodyItem,
  PROVISIONABLE_BOUNDARY_KINDS,
} from "common";
import { useMemo, useState } from "react";

const columnHelper = createColumnHelper<ListCustomerBoundaryResponseBodyItem>();

const defaultParams = {
  sort: [],
  filters: [],
  page: 0,
  pageSize: 10,
};

type ProvisionableBoundaryKind = (typeof PROVISIONABLE_BOUNDARY_KINDS)[number];

const VENDOR_TAB_TITLES: Record<ProvisionableBoundaryKind, string> = {
  [BoundaryKind.GCP]: "Google Cloud",
  [BoundaryKind.AWS]: "AWS",
};

// Derived from the kinds the platform can provision rather than from the kinds this deployment
// happens to hold, so a vendor with no boundaries yet still has a tab to upload its first one
// into, and so a third provisionable kind arrives here without a change to this page. OnPrem is
// absent from that constant by design: an on-prem deployment provisions no boundary at all.
const VENDOR_TABS: Array<Tab<ProvisionableBoundaryKind>> =
  PROVISIONABLE_BOUNDARY_KINDS.map(kind => ({
    name: kind,
    customTitle: VENDOR_TAB_TITLES[kind],
  }));

const CustomerBoundary = () => {
  const [page, setPage] = useState(defaultParams.page);
  const [pageSize, setPageSize] = useState(defaultParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    defaultParams.sort
  );
  const [isHardDeleteSelected, setIsHardDeleteSelected] = useState(false);
  const [isAddCustomerBoundaryModalOpen, setIsAddCustomerBoundaryModalOpen] =
    useState(false);
  const [customerBoundaryToBeDeleted, setCustomerBoundaryToBeDeleted] =
    useState<ListCustomerBoundaryResponseBodyItem[]>([]);
  const [customerBoundaryToBeEdited, setCustomerBoundaryToBeEdited] = useState<
    ListCustomerBoundaryResponseBodyItem[]
  >([]);

  const [selectedBoundaries, setSelectedBoundaries] = useState<
    ListCustomerBoundaryResponseBodyItem[]
  >([]);

  const [activeVendor, setActiveVendor] = useState<ProvisionableBoundaryKind>(
    PROVISIONABLE_BOUNDARY_KINDS[0]
  );

  // Filtered on the server, not in the page. Paging is server side and the endpoint's row total
  // comes from a COUNT(*) OVER() that the same filter applies to, so narrowing the ten rows already
  // fetched would leave the pager and the total describing every vendor while the table showed one.
  const { data, isLoading } = useGetCustomerBoundary(
    page,
    pageSize,
    sort,
    `cloudVendor:eq=${activeVendor}`
  );

  // Page, sort and selection all describe the vendor they were made against. Selection matters
  // most: it deliberately survives paging, so without this it would survive a tab change too, and
  // the delete and edit buttons act on everything selected. That would let a boundary be deleted
  // from a tab it is not visible in.
  const handleVendorChange = (vendor: ProvisionableBoundaryKind): void => {
    if (vendor === activeVendor) return;
    setActiveVendor(vendor);
    setPage(defaultParams.page);
    setSort(defaultParams.sort);
    setSelectedBoundaries([]);
  };

  const {
    mutateAsync: deleteCustomerBoundary,
    isPending: isDeleteCustomerBoundaryLoading,
  } = useDeleteCustomerBoundary();

  const [boundaryToAttach, setBoundaryToAttach] =
    useState<ListCustomerBoundaryResponseBodyItem | null>(null);
  const [boundaryToDetach, setBoundaryToDetach] =
    useState<ListCustomerBoundaryResponseBodyItem | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");

  const { data: organizations } = useGetOrganizations();
  const { mutateAsync: attachCustomerBoundary, isPending: isAttachPending } =
    useAttachCustomerBoundary();
  const { mutateAsync: detachCustomerBoundary, isPending: isDetachPending } =
    useDetachCustomerBoundary();

  /**
   * Resolves false to keep the modal open when the mutation fails, so the operator sees the error
   * notification with the dialog still in front of them rather than a silently closed dialog and a
   * row that did not change.
   */
  const handleConfirmAttach = async (): Promise<boolean> => {
    if (!boundaryToAttach || !selectedOrganizationId) return false;
    try {
      await attachCustomerBoundary({
        customerBoundaryId: boundaryToAttach.id,
        organizationId: selectedOrganizationId,
      });
      setBoundaryToAttach(null);
      setSelectedOrganizationId("");
      return true;
    } catch {
      return false;
    }
  };

  const handleConfirmDetach = async (): Promise<boolean> => {
    // Addressed by the ATTACHMENT id, not the boundary id. They are different rows and the
    // boundary id would revoke nothing.
    if (!boundaryToDetach?.organizationCustomerBoundaryId) return false;
    try {
      await detachCustomerBoundary({
        organizationCustomerBoundaryId:
          boundaryToDetach.organizationCustomerBoundaryId,
      });
      setBoundaryToDetach(null);
      return true;
    } catch {
      return false;
    }
  };

  const allRowsSelected =
    selectedBoundaries.length > 0 &&
    data?.data &&
    selectedBoundaries.length === (data?.data || []).length;

  const someRowsSelected = selectedBoundaries.length > 0 && !allRowsSelected;

  const checkboxColumn = useMemo(
    () =>
      columnHelper.accessor("id", {
        header: () => (
          <IndeterminateCheckbox
            checked={allRowsSelected}
            indeterminate={someRowsSelected}
            onChange={toggleAllRowsSelected}
            onClick={e => {
              e.stopPropagation();
            }}
            className="text-indigo-600"
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            onClick={e => {
              e.stopPropagation();
            }}
            checked={selectedBoundaries.some(
              ({ id }) => id === row.original.id
            )}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={() => {
              row.toggleSelected();
              handleCheckboxToggle(row.original);
            }}
            className="text-indigo-600"
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        size: 50,
      }),
    [selectedBoundaries, allRowsSelected, someRowsSelected, data]
  );

  const columns = useMemo(() => {
    if (!data) return [];
    // The two payloads no longer sit side by side. A row carries only its own kind's fields, so
    // the combined table left roughly fifteen of its twenty-one columns blank on every row and
    // survived only on horizontal scroll. Each tab renders the shared columns plus one vendor's.
    // The cloud vendor column itself is gone, because the tab already names it.
    const gcpColumns = [
      columnHelper.accessor("gcpProjectId", {
        cell: info => info.getValue(),
        header: () => <span>GCP Project Id</span>,
      }),
      columnHelper.accessor("gcpBootDiskSize", {
        cell: info => info.getValue(),
        header: () => <span>GCP Boot Disk Size</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("gcpServiceRegion", {
        cell: info => info.getValue(),
        header: () => <span>GCP Service Region</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("gcpMachineTypes", {
        cell: info => info.getValue(),
        header: () => <span>GCP Machine Types</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("gcpMasterPubdir", {
        cell: info => info.getValue(),
        header: () => <span>GCP Master Pubdir</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("gcpNetwork", {
        cell: info => info.getValue(),
        header: () => <span>GCP Network</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("gcpSubnetwork", {
        cell: info => info.getValue(),
        header: () => <span>GCP Subnetwork</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("gcpMasterDowndir", {
        cell: info => info.getValue(),
        header: () => <span>GCP Master Downdir</span>,
        enableSorting: false,
      }),
    ];

    const awsColumns = [
      columnHelper.accessor("awsRegion", {
        cell: info => info.getValue(),
        header: () => <span>AWS Region</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("awsMasterPubdir", {
        cell: info => info.getValue(),
        header: () => <span>AWS Master Pubdir</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("awsMasterDowndir", {
        cell: info => info.getValue(),
        header: () => <span>AWS Master Downdir</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("awsJobQueue", {
        cell: info => info.getValue(),
        header: () => <span>AWS Job Queue</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("awsCliPath", {
        cell: info => info.getValue(),
        header: () => <span>AWS CLI Path</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("awsJobRole", {
        cell: info => info.getValue(),
        header: () => <span>AWS Job Role</span>,
        enableSorting: false,
      }),
      // aws-2-1. The largest job the compute environment can place; a launch asking for more is refused.
      columnHelper.accessor("awsMaxJobMemoryGb", {
        cell: info => info.getValue() ?? "",
        header: () => <span>AWS Max Job Memory (GB)</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("awsMaxJobCpus", {
        cell: info => info.getValue() ?? "",
        header: () => <span>AWS Max Job vCPUs</span>,
        enableSorting: false,
      }),
    ];

    const cols = [
      columnHelper.accessor("id", {
        cell: info => info.getValue(),
        header: () => <span>Id</span>,
      }),
      ...(activeVendor === BoundaryKind.GCP ? gcpColumns : awsColumns),
      columnHelper.accessor("claimedAt", {
        cell: info => info.getValue(),
        header: () => <span>Claimed At</span>,
      }),
      columnHelper.accessor("claimedUserEmail", {
        cell: info => info.getValue(),
        header: () => <span>Claimed User Email</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("claimedOrganizationName", {
        cell: info => info.getValue(),
        header: () => <span>Claimed Organization Name</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("revokedAt", {
        cell: info => {
          const value = info.getValue();
          if (!value) return <span>N/A</span>;
          return <span>{formatDateString(value, "LLL")}</span>;
        },
        header: () => <span>Revoked At</span>,
      }),
      columnHelper.accessor("provisionedAt", {
        cell: info => {
          const value = info.getValue() as Date;
          if (!value) return <span>N/A</span>;
          return <span>{formatDateString(value, "LLL")}</span>;
        },
        header: () => <span>Provisioned At</span>,
      }),
      // Per row rather than a header button over the checkbox selection, unlike Edit and Delete.
      // Attaching binds ONE boundary to ONE organization, so a multi-row selection has no sensible
      // answer to "which organization", and the action that applies differs from row to row.
      columnHelper.display({
        id: "actions",
        header: () => <span>Actions</span>,
        // Detach only applies to a LIVE attachment. A revoked one keeps its
        // organizationCustomerBoundaryId for provenance, and detaching it again can only refuse.
        cell: ({ row }) =>
          row.original.organizationCustomerBoundaryId &&
          !row.original.revokedAt ? (
            <button
              onClick={() => setBoundaryToDetach(row.original)}
              className="font-semibold text-red-600 hover:text-red-500">
              Detach
            </button>
          ) : (
            <button
              onClick={() => {
                setSelectedOrganizationId("");
                setBoundaryToAttach(row.original);
              }}
              className="font-semibold text-indigo-600 hover:text-indigo-500">
              Attach
            </button>
          ),
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ] as Array<ColumnDef<ListCustomerBoundaryResponseBodyItem>>;
    return [checkboxColumn, ...cols];
  }, [data, checkboxColumn, activeVendor]);

  const toggleAllRowsSelected = (): void => {
    const currentPageIds = data?.data.map(item => item.id);
    const isAllSelected = data?.data.every(item =>
      selectedBoundaries.some(selected => selected.id === item.id)
    );

    if (isAllSelected) {
      // Deselect all files on this page
      setSelectedBoundaries(
        prev => prev.filter(item => !currentPageIds?.includes(item.id)) || []
      );
    } else {
      // Select all files on this page (add any not already selected)
      const newSelections =
        data?.data.filter(
          item => !selectedBoundaries.some(selected => selected.id === item.id)
        ) || [];
      setSelectedBoundaries(prev => [...prev, ...newSelections]);
    }
  };

  const handleCheckboxToggle = (
    row: ListCustomerBoundaryResponseBodyItem
  ): void => {
    const isSelected = selectedBoundaries.some(item => item.id === row.id);
    if (isSelected) {
      setSelectedBoundaries(prev => prev.filter(item => item.id !== row.id));
    } else {
      setSelectedBoundaries(prev => [...prev, row]);
    }
  };

  const handleOpenDeleteModal = () => {
    setCustomerBoundaryToBeDeleted(selectedBoundaries);
  };

  const handleConfirmDelete = async () => {
    try {
      await Promise.all(
        customerBoundaryToBeDeleted.map(item =>
          deleteCustomerBoundary({
            id: item.id,
            hardDelete: isHardDeleteSelected,
          })
        )
      );
      setSelectedBoundaries([]);
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setCustomerBoundaryToBeDeleted([]);
      setIsHardDeleteSelected(false);
    }
  };

  const handleOpenEditModal = async () => {
    setCustomerBoundaryToBeEdited(selectedBoundaries);
  };

  return (
    <main className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <PageHeading title="Customer Boundary" />
        <div className="flex items-center">
          <ActionButton
            button={BUTTONS.ADD_CUSTOMER_BOUNDARY}
            primary={true}
            onClick={() => setIsAddCustomerBoundaryModalOpen(true)}
          />
          <button
            disabled={!selectedBoundaries.length}
            onClick={handleOpenEditModal}
            className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Edit Customer Boundary
          </button>
          <button
            disabled={!selectedBoundaries.length}
            onClick={handleOpenDeleteModal}
            className="ml-4 inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
            Delete Customer Boundary
          </button>
        </div>
      </div>
      <div>
        <HorizontalTabs
          buttonIdPrefix="customer-boundary-vendor"
          options={VENDOR_TABS}
          activeTab={activeVendor}
          onTabChange={handleVendorChange}
        />
        <div className="overflow-auto">
          <Table
            isLoading={isLoading}
            data={data?.data || []}
            columns={columns as any}
            sort={sort}
            setSort={setSort}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total={data?.total || 0}
            emptyMessage="No data available."
            applyColumnSize
            customTableClassName="w-max whitespace-nowrap"
          />
        </div>
      </div>
      {isAddCustomerBoundaryModalOpen && (
        <AddCustomerBoundary
          isOpen={isAddCustomerBoundaryModalOpen}
          onClose={() => setIsAddCustomerBoundaryModalOpen(false)}
        />
      )}
      {customerBoundaryToBeDeleted?.length > 0 && (
        <DeleteModal
          item={customerBoundaryToBeDeleted}
          setItem={setCustomerBoundaryToBeDeleted}
          deleteFunction={handleConfirmDelete}
          isLoading={isDeleteCustomerBoundaryLoading}
          title="Delete Customer Boundary"
          message={() => (
            <>
              Are you sure you want to delete{" "}
              <strong className="font-semibold">
                {customerBoundaryToBeDeleted.map(item => item?.id).join(", ")}
              </strong>
              ?
              <div className="mt-3 flex flex-row items-center">
                <label className="block pr-4 text-sm leading-6 text-gray-900">
                  I want to delete permanently
                </label>
                <IndeterminateCheckbox
                  checked={isHardDeleteSelected}
                  onChange={() =>
                    setIsHardDeleteSelected(!isHardDeleteSelected)
                  }
                  onClick={e => {
                    e.stopPropagation();
                  }}
                />
              </div>
            </>
          )}
        />
      )}
      {boundaryToAttach && (
        <Modal
          open={!!boundaryToAttach}
          setOpen={open => {
            if (!open) setBoundaryToAttach(null);
          }}
          title="Attach Customer Boundary"
          confirmText="Attach Boundary"
          isConfirmDisabled={!selectedOrganizationId}
          isConfirmLoading={isAttachPending}
          onConfirm={handleConfirmAttach}>
          <div className="mt-2">
            <p className="text-sm text-gray-600">
              Attach{" "}
              <strong className="font-semibold">{boundaryToAttach.id}</strong>{" "}
              to an organization. The organization keeps any data sources it
              already has.
            </p>
            <label
              htmlFor="attach-organization"
              className="mt-4 block text-sm font-medium leading-6 text-gray-900">
              Organization
            </label>
            <select
              id="attach-organization"
              value={selectedOrganizationId}
              onChange={event => setSelectedOrganizationId(event.target.value)}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm">
              <option value="">Select an organization</option>
              {(organizations ?? []).map(organization => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </div>
        </Modal>
      )}
      {boundaryToDetach && (
        <Modal
          open={!!boundaryToDetach}
          setOpen={open => {
            if (!open) setBoundaryToDetach(null);
          }}
          title="Detach Customer Boundary"
          confirmText="Detach Boundary"
          confirmButtonColor={ButtonColor.Red}
          isConfirmLoading={isDetachPending}
          onConfirm={handleConfirmDetach}>
          <p className="mt-2 text-sm text-gray-600">
            Revoke{" "}
            <strong className="font-semibold">
              {boundaryToDetach.claimedOrganizationName ?? "this organization"}
            </strong>
            &apos;s claim on{" "}
            <strong className="font-semibold">{boundaryToDetach.id}</strong>?
            Any run environment still bound to it must be unbound first.
          </p>
        </Modal>
      )}
      {customerBoundaryToBeEdited?.length > 0 && (
        <EditCustomerBoundary
          customerBoundaryFiles={customerBoundaryToBeEdited}
          isOpen={!!customerBoundaryToBeEdited}
          isLoading={isLoading}
          onClose={() => {
            setCustomerBoundaryToBeEdited([]);
            setSelectedBoundaries([]);
          }}
        />
      )}
    </main>
  );
};

export default CustomerBoundary;
